/**
 * src/lib/tenancy.js — resolving a studio from its own subdomain (TS-28).
 *
 * Shared between the React app (which studio is this public page for, once
 * it's not in the URL path any more?) and worker.js (which 301s a legacy
 * `/book/:slug` or `/gallery/:slug` link to that studio's subdomain) — both
 * need the exact same notion of "is this a studio subdomain, and which one",
 * so it lives in one place rather than two.
 *
 * VITE_APP_BASE_DOMAIN is unset in dev and on the legacy shared origin, and
 * every function here just returns null in that case rather than throwing,
 * so callers fall back to their pre-subdomain behaviour unchanged.
 */

/** Mirrors studios.slug's own validation — see api/src/routes/studios.js. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Extracts a studio slug from a page's hostname, if it is exactly one valid
 * label under baseDomain.
 *
 * @param {string} hostname e.g. window.location.hostname.
 * @param {string|undefined} baseDomain import.meta.env.VITE_APP_BASE_DOMAIN.
 * @returns {string|null}
 */
export function studioSlugFromHostname(hostname, baseDomain) {
  if (!hostname || !baseDomain) return null;
  const suffix = `.${baseDomain.toLowerCase()}`;
  const host = hostname.toLowerCase();
  if (!host.endsWith(suffix)) return null;
  const label = host.slice(0, -suffix.length);
  if (!label || !SLUG_PATTERN.test(label)) return null;
  return label;
}

/**
 * Where a legacy `/book/:slug...` or `/gallery/:slug` link should permanently
 * redirect to, once that studio has its own subdomain — or null if this path
 * is not one of those (including the new slug-free `/book` / `/gallery`
 * forms, which need no redirect).
 *
 * `/book/confirmed` is the slug-free portal's own post-payment page, not a
 * studio called "confirmed" — it's where the first redirect of a Stripe
 * return (`/book/<slug>/confirmed`) lands. Reading it as a slug sent every
 * client who'd just paid a deposit on to `confirmed.<domain>`, a studio that
 * doesn't exist. `confirmed` is a reserved slug in the API for the same
 * reason (api/src/lib/tenancy.js), so no real studio can hold it.
 *
 * @param {string} pathname e.g. "/book/ink-studio/confirmed".
 * @param {string} search e.g. "?appointment=abc" (with leading '?', or '').
 * @param {string|undefined} baseDomain
 * @returns {string|null} A full https:// URL.
 */
export function legacyRedirectTarget(pathname, search, baseDomain) {
  if (!baseDomain) return null;

  const book = pathname.match(/^\/book\/([a-z0-9]+(?:-[a-z0-9]+)*)((?:\/.*)?)$/i);
  if (book && book[1].toLowerCase() !== 'confirmed') {
    const [, slug, rest] = book;
    return `https://${slug}.${baseDomain}/book${rest}${search}`;
  }

  const gallery = pathname.match(/^\/gallery\/([a-z0-9]+(?:-[a-z0-9]+)*)$/i);
  if (gallery) {
    const [, slug] = gallery;
    return `https://${slug}.${baseDomain}/gallery${search}`;
  }

  return null;
}
